import * as React from "react";
import {
  alpha,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
  FormControlLabel,
  FormGroup,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SaveIcon from "@mui/icons-material/Save";
import BookmarkBorderIcon from "@mui/icons-material/BookmarkBorder";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TuneIcon from "@mui/icons-material/Tune";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import BlockIcon from "@mui/icons-material/Block";
import { toast } from "react-toastify";

// Import API functions directly
import {
  deleteFilterPreset,
  getFilterPresets,
  postFilterPreset,
} from "../../../apis";

function FilterToolbar({ onFilterApply, onClearFilters, filterModel }) {
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = React.useState(false);
  const [presetName, setPresetName] = React.useState("");
  const [savedPresets, setSavedPresets] = React.useState([]);
  const [presetsMenuAnchor, setPresetsMenuAnchor] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);
  const [pendingFilterModel, setPendingFilterModel] =
    React.useState(filterModel);

  // Fetch presets on component mount
  React.useEffect(() => {
    loadPresets();
  }, []);

  // Update pending filters when drawer opens
  React.useEffect(() => {
    if (drawerOpen) {
      setPendingFilterModel(filterModel);
    }
  }, [drawerOpen, filterModel]);

  const loadPresets = async () => {
    setIsLoading(true);
    try {
      const response = await getFilterPresets();
      setSavedPresets(response.data);
    } catch (error) {
      console.error("Failed to load filter presets:", error);
      toast.error(
        error.response?.data?.message || "Failed to load filter presets"
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Function to filter out inactive filters
  const getActiveFiltersOnly = (filterModel) => {
    return filterModel
      .map((category) => {
        // For button type filters, only include active ones
        if (category.type === "button") {
          const activeFilters = category.filters.filter((f) => f.active);
          // Only return the category if it has active filters
          if (activeFilters.length > 0) {
            return {
              ...category,
              filters: activeFilters,
            };
          }
          return null;
        }

        // For input type filters, only include if there's a value
        if (category.type === "input") {
          const hasValue = category.filters.some((f) => f.active && f.value);
          if (hasValue) {
            return {
              ...category,
              filters: category.filters.filter((f) => f.active && f.value),
            };
          }
          return null;
        }

        return null;
      })
      .filter(Boolean); // Remove null entries
  };

  const handleSavePreset = async () => {
    if (!presetName.trim()) return;

    setIsSaving(true);
    try {
      const activeFilters = getActiveFiltersOnly(filterModel);

      if (activeFilters.length === 0) {
        toast.warning("No active filters to save");
        setIsSaving(false);
        return;
      }

      const response = await postFilterPreset({
        name: presetName.trim(),
        filters: activeFilters,
      });

      setSavedPresets([...savedPresets, response.data]);
      setPresetName("");
      setSaveDialogOpen(false);
      toast.success(`Filter preset "${response.data.name}" saved successfully`);
    } catch (error) {
      console.error("Failed to save filter preset:", error);
      toast.error(
        error.response?.data?.message || "Failed to save filter preset"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleLoadPreset = (preset) => {
    const updatedFilterModel = filterModel.map((category) => {
      const presetCategory = preset.filters.find(
        (pc) => pc.label === category.label
      );

      if (presetCategory) {
        return {
          ...category,
          filters: category.filters.map((filter) => {
            const presetFilter = presetCategory.filters.find(
              (pf) => pf.value === filter.value
            );
            if (presetFilter) {
              return {
                ...filter,
                active: true,
                value: presetFilter.value || filter.value,
                exclude: presetFilter.exclude || false,
              };
            }
            return {
              ...filter,
              active: false,
              value: filter.value || "",
              exclude: false,
            };
          }),
        };
      }

      return {
        ...category,
        filters: category.filters.map((filter) => ({
          ...filter,
          active: false,
          value: filter.value || "",
          exclude: false,
        })),
      };
    });

    setPendingFilterModel(updatedFilterModel);
    setPresetsMenuAnchor(null);
  };

  const handleDeletePreset = async (presetId, event) => {
    event.stopPropagation();

    try {
      await deleteFilterPreset(presetId);
      setSavedPresets(savedPresets.filter((p) => p.id !== presetId));
      toast.success("Filter preset deleted successfully");
    } catch (error) {
      console.error("Failed to delete filter preset:", error);
      toast.error(
        error.response?.data?.message || "Failed to delete filter preset"
      );
    }
  };

  const handleApplyFilters = () => {
    const hasActiveFilters = pendingFilterModel.some((category) =>
      category.filters.some((f) => f.active)
    );
    if (hasActiveFilters) {
      onFilterApply(pendingFilterModel);
    }
    setDrawerOpen(false);
  };

  const activeFiltersCount = React.useMemo(() => {
    return filterModel.reduce(
      (count, category) =>
        count + category.filters.filter((f) => f.active).length,
      0
    );
  }, [filterModel]);

  const pendingFiltersCount = React.useMemo(() => {
    return pendingFilterModel.reduce(
      (count, category) =>
        count + category.filters.filter((f) => f.active).length,
      0
    );
  }, [pendingFilterModel]);

  const filterExplanations = {
    Pathogenicity:
      "Filter variants by their predicted pathogenic impact on protein function",
    Conservation:
      "Filter based on evolutionary conservation scores across species",
    Frequency: "Filter by population allele frequency in genomic databases",
    Impact: "Filter by predicted functional impact on genes and proteins",
    Gene: "Search for variants in specific genes by name or symbol",
    Position: "Filter variants by genomic position or coordinate range",
    Zygosity: "Filter variants by zygosity status (Homozygous or Heterozygous)",
    Father_Zygosity: "Filter variants by father's zygosity status",
    Mother_Zygosity: "Filter variants by mother's zygosity status",
    ACMG: "Filter by ACMG classification (click to include, right-click to exclude)",
    AMP: "Filter by AMP tier classification (click to include, right-click to exclude)",
    ClinVar:
      "Filter by ClinVar significance (click to include, right-click to exclude)",
  };

  const categoryColors = {
    Pathogenicity: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    Conservation: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    Frequency: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    Impact: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    Gene: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    Position: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    Zygosity: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    Father_Zygosity: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    Mother_Zygosity: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    ACMG: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    AMP: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
    ClinVar: {
      main: "#2196f3",
      gradient: "linear-gradient(135deg, #2196f3 0%, #2196f3 100%)",
      shadow: "rgba(33, 150, 243, 0.12)",
    },
  };

  const getColorForCategory = (label) => {
    return (
      categoryColors[label] || {
        main: "#428df5",
        gradient: "linear-gradient(135deg, #64b5f6 0%, #428df5 100%)",
        shadow: "rgba(66, 141, 245, 0.2)",
      }
    );
  };

  const handlePendingFilterChange = (updatedFilterModel) => {
    setPendingFilterModel(updatedFilterModel);
  };

  const handleFilterChipClick = (categoryLabel, filterValue, event) => {
    // Only allow exclude mode for ACMG, AMP, and ClinVar filters
    const isExcludableCategory = ["ACMG", "AMP", "ClinVar"].includes(
      categoryLabel
    );

    // Right click or ctrl+click to toggle exclude mode (only for excludable categories)
    const shouldToggleExclude =
      isExcludableCategory && (event.button === 2 || event.ctrlKey);

    event.preventDefault();

    handlePendingFilterChange(
      pendingFilterModel.map((c) =>
        c.label === categoryLabel
          ? {
              ...c,
              filters: c.filters.map((f) =>
                f.value === filterValue
                  ? shouldToggleExclude
                    ? {
                        ...f,
                        active: true,
                        exclude: f.active ? !f.exclude : true,
                      }
                    : { ...f, active: !f.active, exclude: false }
                  : f
              ),
            }
          : c
      )
    );
  };

  return (
    <>
      {/* Compact Filter Button */}
      <Box sx={{ mb: 1, display: "flex", gap: 2, alignItems: "center" }}>
        <Button
          variant="outlined"
          startIcon={<TuneIcon />}
          onClick={() => setDrawerOpen(true)}
          sx={{
            borderColor: "#bdbdbd",
            color: "#333",
            fontWeight: 500,
            textTransform: "none",
            background: "transparent",
            boxShadow: "none",
            "&:hover": {
              borderColor: "#2196f3",
              backgroundColor: alpha("#2196f3", 0.08),
              color: "#2196f3",
            },
          }}
        >
          Filters
          {activeFiltersCount > 0 && (
            <Chip
              label={activeFiltersCount}
              size="small"
              sx={{
                ml: 1,
                height: 22,
                backgroundColor: "#e3f2fd",
                color: "#2196f3",
                fontWeight: 700,
                fontSize: "0.75rem",
                border: "none",
              }}
            />
          )}
        </Button>

        <Button
          variant="outlined"
          startIcon={
            isLoading ? <CircularProgress size={16} /> : <BookmarkBorderIcon />
          }
          onClick={(e) => setPresetsMenuAnchor(e.currentTarget)}
          disabled={savedPresets.length === 0 || isLoading}
          sx={{
            textTransform: "none",
            borderColor: "#bdbdbd",
            color: savedPresets.length > 0 ? "#333" : "#999",
            fontWeight: 500,
            background: "transparent",
            "&:hover": {
              borderColor: "#2196f3",
              backgroundColor: alpha("#2196f3", 0.08),
              color: "#2196f3",
            },
          }}
        >
          Saved Filters ({savedPresets.length})
        </Button>

        {activeFiltersCount > 0 && (
          <Button
            variant="outlined"
            onClick={() => onClearFilters()}
            sx={{
              textTransform: "none",
              color: "#666",
              borderColor: "#bdbdbd",
              fontWeight: 500,
              background: "transparent",
              "&:hover": {
                borderColor: "#2196f3",
                backgroundColor: alpha("#2196f3", 0.08),
                color: "#2196f3",
              },
            }}
          >
            Clear All
          </Button>
        )}
      </Box>

      {/* Filter Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 520 },
            backgroundColor: "#fafbfc",
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2.5,
              background: "#f5f5f5",
              color: "#333",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              boxShadow: "none",
              borderBottom: "1px solid #e0e0e0",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <TuneIcon sx={{ fontSize: 26, color: "#2196f3" }} />
              <Typography variant="h6" fontWeight={700}>
                Filter Variants
              </Typography>
              {pendingFiltersCount > 0 && (
                <Chip
                  label={`${pendingFiltersCount} selected`}
                  size="small"
                  sx={{
                    backgroundColor: "#e3f2fd",
                    color: "#2196f3",
                    fontWeight: 700,
                  }}
                />
              )}
            </Box>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              size="small"
              sx={{
                color: "#333",
                "&:hover": { backgroundColor: alpha("#2196f3", 0.08) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Filters Content */}
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
            <Stack spacing={1.5}>
              {/* Regular Filters (non-zygosity, non-select) */}
              {pendingFilterModel
                .filter(
                  (c) => c.type === "button" && !c.label.includes("Zygosity")
                )
                .map((category) => {
                  const colors = getColorForCategory(category.label);
                  return (
                    <Paper
                      key={category.label}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "white",
                        border: "1px solid #bdbdbd",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: "#2196f3",
                          boxShadow: `0 4px 20px ${colors.shadow}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 3,
                            height: 20,
                            borderRadius: 1.5,
                            background: "#2196f3",
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{
                            color: "#2c3e50",
                            flex: 1,
                            fontSize: "0.875rem",
                          }}
                        >
                          {category.label.replace("_", " ")}
                        </Typography>
                        <Tooltip
                          title={
                            filterExplanations[category.label] ||
                            "Filter variants by this category"
                          }
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            sx={{ fontSize: 18, color: "#95a5a6" }}
                          />
                        </Tooltip>
                      </Box>

                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                        {category.filters.map((filter) => (
                          <Chip
                            key={filter.value}
                            label={filter.label}
                            onClick={(e) =>
                              handleFilterChipClick(
                                category.label,
                                filter.value,
                                e
                              )
                            }
                            onContextMenu={(e) =>
                              handleFilterChipClick(
                                category.label,
                                filter.value,
                                e
                              )
                            }
                            icon={
                              filter.active ? (
                                filter.exclude ? (
                                  <BlockIcon sx={{ fontSize: 16 }} />
                                ) : (
                                  <CheckCircleIcon sx={{ fontSize: 16 }} />
                                )
                              ) : (
                                <RadioButtonUncheckedIcon
                                  sx={{ fontSize: 16 }}
                                />
                              )
                            }
                            sx={{
                              height: 32,
                              fontSize: "0.8125rem",
                              fontWeight: filter.active ? 700 : 500,
                              background: filter.active
                                ? filter.exclude
                                  ? alpha("#f44336", 0.08)
                                  : alpha("#2196f3", 0.08)
                                : "white",
                              color: filter.active
                                ? filter.exclude
                                  ? "#f44336"
                                  : "#2196f3"
                                : "#2c3e50",
                              border: filter.active
                                ? filter.exclude
                                  ? "2px solid #f44336"
                                  : "2px solid #2196f3"
                                : "1.5px solid #bdbdbd",
                              boxShadow: "none",
                              transition: "all 0.2s ease",
                              cursor: "pointer",
                              "&:hover": {
                                background: filter.exclude
                                  ? alpha("#f44336", 0.08)
                                  : alpha("#2196f3", 0.08),
                                borderColor: filter.exclude
                                  ? "#f44336"
                                  : "#2196f3",
                                color: filter.exclude ? "#f44336" : "#2196f3",
                                transform: "translateY(-1px)",
                              },
                              "& .MuiChip-icon": {
                                color: filter.active
                                  ? filter.exclude
                                    ? "#f44336"
                                    : "#2196f3"
                                  : "#2196f3",
                              },
                            }}
                          />
                        ))}
                      </Box>
                      {(category.label === "ACMG" ||
                        category.label === "AMP" ||
                        category.label === "ClinVar") && (
                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 1,
                            color: "#666",
                            fontSize: "0.7rem",
                          }}
                        >
                          Tip: Right-click or Ctrl+click to exclude a filter
                        </Typography>
                      )}
                    </Paper>
                  );
                })}

              {/* Select Filters (Consequence) */}
              {pendingFilterModel
                .filter((c) => c.type === "select")
                .map((category) => {
                  const colors = getColorForCategory(category.label);
                  const activeCount = category.filters.filter(
                    (f) => f.active
                  ).length;
                  return (
                    <Paper
                      key={category.label}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "white",
                        border: "1px solid #bdbdbd",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: "#2196f3",
                          boxShadow: `0 4px 20px ${colors.shadow}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 3,
                            height: 20,
                            borderRadius: 1.5,
                            background: "#2196f3",
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{
                            color: "#2c3e50",
                            flex: 1,
                            fontSize: "0.875rem",
                          }}
                        >
                          {category.label.replace("_", " ")}
                          {activeCount > 0 && (
                            <Chip
                              label={activeCount}
                              size="small"
                              sx={{
                                ml: 1,
                                height: 20,
                                fontSize: "0.7rem",
                                backgroundColor: "#e3f2fd",
                                color: "#2196f3",
                                fontWeight: 700,
                              }}
                            />
                          )}
                        </Typography>
                        <Tooltip
                          title={
                            filterExplanations[category.label] ||
                            "Filter variants by this category"
                          }
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            sx={{ fontSize: 18, color: "#95a5a6" }}
                          />
                        </Tooltip>
                      </Box>

                      <Box
                        sx={{
                          maxHeight: 300,
                          overflowY: "auto",
                          border: "1px solid #e0e0e0",
                          borderRadius: 1,
                          p: 1,
                          backgroundColor: "#fafafa",
                        }}
                      >
                        <FormGroup>
                          {category.filters.map((filter) => (
                            <FormControlLabel
                              key={filter.value}
                              control={
                                <Checkbox
                                  checked={filter.active}
                                  onChange={() =>
                                    handlePendingFilterChange(
                                      pendingFilterModel.map((c) =>
                                        c.label === category.label
                                          ? {
                                              ...c,
                                              filters: c.filters.map((f) =>
                                                f.value === filter.value
                                                  ? { ...f, active: !f.active }
                                                  : f
                                              ),
                                            }
                                          : c
                                      )
                                    )
                                  }
                                  sx={{
                                    color: "#bdbdbd",
                                    "&.Mui-checked": {
                                      color: "#2196f3",
                                    },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  sx={{
                                    fontSize: "0.8125rem",
                                    color: filter.active
                                      ? "#2196f3"
                                      : "#2c3e50",
                                    fontWeight: filter.active ? 600 : 400,
                                  }}
                                >
                                  {filter.label}
                                </Typography>
                              }
                              sx={{
                                ml: 0,
                                py: 0.5,
                                "&:hover": {
                                  backgroundColor: alpha("#2196f3", 0.04),
                                  borderRadius: 1,
                                },
                              }}
                            />
                          ))}
                        </FormGroup>
                      </Box>
                    </Paper>
                  );
                })}

              {/* Grouped Zygosity Filters */}
              {pendingFilterModel.filter((c) => c.label.includes("Zygosity"))
                .length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "white",
                    border: "1px solid #bdbdbd",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      borderColor: "#2196f3",
                      boxShadow: "0 4px 20px rgba(33, 150, 243, 0.12)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 3,
                        height: 20,
                        borderRadius: 1.5,
                        background: "#2196f3",
                      }}
                    />
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      sx={{
                        color: "#2c3e50",
                        flex: 1,
                        fontSize: "0.875rem",
                      }}
                    >
                      Zygosity Filters
                    </Typography>
                    <Tooltip
                      title="Filter variants by zygosity status for proband, father, and mother"
                      arrow
                      placement="top"
                    >
                      <InfoOutlinedIcon
                        sx={{ fontSize: 18, color: "#95a5a6" }}
                      />
                    </Tooltip>
                  </Box>

                  {/* Proband, Father, Mother Zygosity filters */}
                  <Stack spacing={1.25}>
                    {pendingFilterModel
                      .filter((c) => c.label.includes("Zygosity"))
                      .map((category) => (
                        <Box key={category.label}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: "#666",
                              fontWeight: 600,
                              mb: 0.5,
                              display: "block",
                              fontSize: "0.75rem",
                            }}
                          >
                            {category.label === "Zygosity"
                              ? "Proband"
                              : category.label.replace("_Zygosity", "")}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 0.75,
                            }}
                          >
                            {category.filters.map((filter) => (
                              <Chip
                                key={filter.value}
                                label={filter.label}
                                onClick={() =>
                                  handlePendingFilterChange(
                                    pendingFilterModel.map((c) =>
                                      c.label === category.label
                                        ? {
                                            ...c,
                                            filters: c.filters.map((f) =>
                                              f.value === filter.value
                                                ? { ...f, active: !f.active }
                                                : f
                                            ),
                                          }
                                        : c
                                    )
                                  )
                                }
                                icon={
                                  filter.active ? (
                                    <CheckCircleIcon sx={{ fontSize: 14 }} />
                                  ) : (
                                    <RadioButtonUncheckedIcon
                                      sx={{ fontSize: 14 }}
                                    />
                                  )
                                }
                                sx={{
                                  height: 28,
                                  fontSize: "0.75rem",
                                  fontWeight: filter.active ? 700 : 500,
                                  background: filter.active
                                    ? alpha("#2196f3", 0.08)
                                    : "white",
                                  color: filter.active ? "#2196f3" : "#2c3e50",
                                  border: filter.active
                                    ? "2px solid #2196f3"
                                    : "1.5px solid #bdbdbd",
                                  boxShadow: "none",
                                  transition: "all 0.2s ease",
                                  cursor: "pointer",
                                  "&:hover": {
                                    background: alpha("#2196f3", 0.08),
                                    borderColor: "#2196f3",
                                    color: "#2196f3",
                                    transform: "translateY(-1px)",
                                  },
                                  "& .MuiChip-icon": {
                                    color: "#2196f3",
                                    marginLeft: "6px",
                                    marginRight: "-4px",
                                  },
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      ))}
                  </Stack>
                </Paper>
              )}

              {/* Input Filters */}
              {pendingFilterModel
                .filter((c) => c.type === "input")
                .map((category) => {
                  const colors = getColorForCategory(category.label);
                  return (
                    <Paper
                      key={category.label}
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        backgroundColor: "white",
                        border: "1px solid #bdbdbd",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          borderColor: "#2196f3",
                          boxShadow: `0 4px 20px ${colors.shadow}`,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 1.5,
                        }}
                      >
                        <Box
                          sx={{
                            width: 3,
                            height: 20,
                            borderRadius: 1.5,
                            background: "#2196f3",
                          }}
                        />
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          sx={{
                            color: "#2c3e50",
                            flex: 1,
                            fontSize: "0.875rem",
                          }}
                        >
                          {category.label.replace("_", " ")}
                        </Typography>
                        <Tooltip
                          title={
                            filterExplanations[category.label] ||
                            "Search and filter by this field"
                          }
                          arrow
                          placement="top"
                        >
                          <InfoOutlinedIcon
                            sx={{ fontSize: 18, color: "#95a5a6" }}
                          />
                        </Tooltip>
                      </Box>
                      <TextField
                        fullWidth
                        size="small"
                        variant="outlined"
                        placeholder={`Enter ${category.label
                          .replace("_", " ")
                          .toLowerCase()}...`}
                        value={category.filters[0]?.value || ""}
                        onChange={(e) =>
                          handlePendingFilterChange(
                            pendingFilterModel.map((c) =>
                              c.label === category.label
                                ? {
                                    ...c,
                                    filters: c.filters.map((f) => ({
                                      ...f,
                                      value: e.target.value,
                                      active: Boolean(e.target.value),
                                    })),
                                  }
                                : c
                            )
                          )
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 1.5,
                            backgroundColor: "#f8f9fa",
                            fontSize: "0.875rem",
                            "&:hover fieldset": {
                              borderColor: "#2196f3",
                            },
                            "&.Mui-focused": {
                              backgroundColor: "white",
                              "& fieldset": {
                                borderColor: "#2196f3",
                                borderWidth: 2,
                              },
                            },
                          },
                        }}
                      />
                    </Paper>
                  );
                })}
            </Stack>
          </Box>

          {/* Footer Actions */}
          <Box
            sx={{
              p: 2,
              backgroundColor: "white",
              borderTop: "2px solid #f0f0f0",
              display: "flex",
              gap: 1.5,
            }}
          >
            <Button
              fullWidth
              variant="outlined"
              startIcon={<SaveIcon />}
              onClick={() => setSaveDialogOpen(true)}
              disabled={activeFiltersCount === 0}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderColor: "#bdbdbd",
                color: "#333",
                background: "transparent",
                "&:hover": {
                  borderColor: "#2196f3",
                  color: "#2196f3",
                  backgroundColor: alpha("#2196f3", 0.08),
                },
                "&:disabled": {
                  borderColor: "#e0e0e0",
                  color: "#999",
                },
              }}
            >
              Save Preset
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={onClearFilters}
              disabled={pendingFiltersCount === 0}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderColor: "#bdbdbd",
                color: "#333",
                background: "transparent",
                "&:hover": {
                  borderColor: "#2196f3",
                  color: "#2196f3",
                  backgroundColor: alpha("#2196f3", 0.08),
                },
                "&:disabled": {
                  borderColor: "#e0e0e0",
                  color: "#999",
                },
              }}
            >
              Clear All
            </Button>
            <Button
              fullWidth
              variant="outlined"
              onClick={handleApplyFilters}
              sx={{
                textTransform: "none",
                fontWeight: 500,
                borderColor: "#bdbdbd",
                color: "#333",
                background: "transparent",
                "&:hover": {
                  borderColor: "#2196f3",
                  color: "#2196f3",
                  backgroundColor: alpha("#2196f3", 0.08),
                },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* Save Preset Dialog */}
      <Dialog
        open={saveDialogOpen}
        onClose={() => !isSaving && setSaveDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        <DialogTitle
          sx={{
            background: "#f5f5f5",
            color: "#333",
            fontWeight: 700,
            borderBottom: "1px solid #e0e0e0",
          }}
        >
          Save Filter Preset
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <TextField
            autoFocus
            fullWidth
            label="Preset Name"
            placeholder="e.g., High Impact Variants"
            value={presetName}
            onChange={(e) => setPresetName(e.target.value)}
            disabled={isSaving}
            sx={{
              mt: 1,
              "& .MuiOutlinedInput-root": {
                "&.Mui-focused fieldset": {
                  borderColor: "#2196f3",
                  borderWidth: 2,
                },
              },
              "& .MuiInputLabel-root.Mui-focused": {
                color: "#2196f3",
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button
            onClick={() => setSaveDialogOpen(false)}
            disabled={isSaving}
            sx={{
              textTransform: "none",
              fontWeight: 500,
              color: "#666",
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSavePreset}
            variant="outlined"
            disabled={!presetName.trim() || isSaving}
            startIcon={
              isSaving && <CircularProgress size={16} color="inherit" />
            }
            sx={{
              textTransform: "none",
              fontWeight: 500,
              borderColor: "#bdbdbd",
              color: "#333",
              background: "transparent",
              "&:hover": {
                borderColor: "#2196f3",
                color: "#2196f3",
                backgroundColor: alpha("#2196f3", 0.08),
              },
              "&:disabled": {
                borderColor: "#e0e0e0",
                color: "#999",
              },
            }}
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Presets Menu */}
      <Menu
        anchorEl={presetsMenuAnchor}
        open={Boolean(presetsMenuAnchor)}
        onClose={() => setPresetsMenuAnchor(null)}
        PaperProps={{
          sx: {
            minWidth: 280,
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
          },
        }}
      >
        {savedPresets.map((preset) => (
          <MenuItem
            key={preset.id}
            onClick={() => handleLoadPreset(preset)}
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              py: 1.5,
              px: 2,
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <ListItemIcon sx={{ minWidth: 36 }}>
                <BookmarkIcon
                  sx={{
                    fontSize: 22,
                    color: "#bdbdbd",
                  }}
                />
              </ListItemIcon>
              <ListItemText
                primary={preset.name}
                primaryTypographyProps={{
                  fontWeight: 600,
                  color: "#333",
                }}
              />
            </Box>
            <IconButton
              size="small"
              onClick={(e) => handleDeletePreset(preset.id, e)}
              sx={{
                ml: 2,
                color: "#888",
                "&:hover": {
                  backgroundColor: "#f5f5f5",
                  color: "#f44336",
                },
              }}
            >
              <DeleteOutlineIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default FilterToolbar;
